import { useCallback, useMemo, useState } from "react";
import { Users } from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import { getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import NewContactButton from "routes/contact/components/NewContactButton";
import ContactsActionButtons from "routes/contact/components/contacts_table/ContactsActionButtons";
import ContactService from "services/Contact";
import CampaignTargetService from "services/CampaignTarget";
import CampaignService from "services/Campaign";
import { useCampaignContactTable } from "hooks/useCampaignContactTable";
import { omitByNil } from "helpers/tables";

export const CAMPAIGN_CONTACTS_TABLE_CONSTANTS = {
  tableName: "campaign_contacts",
  className: "Contact",
  exportTableName: "ContactsTable",
  header: "Contacts",
  headerIcon: Users,
};

export const isCampaignTargetContact = (contact, campaignId) =>
  contact.campaign_targets?.some(c => c.campaign_id === campaignId) ?? false;

const CampaignContactsTableV3 = ({
  entityId = null,
  campaignId = null,
  activeSession = null,
  tableName = CAMPAIGN_CONTACTS_TABLE_CONSTANTS.tableName,
}) => {
  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.contactTags.create,
      ACL_RELATIONSHIPS.contactTags.read,
      ACL_RELATIONSHIPS.contactTags.delete,
    ],
    { partialMatch: true }
  );

  const [includedInCampaign, setIncludedInCampaign] = useState({});
  const defaultQueryParams = useMemo(() => {
    return omitByNil({
      entity_id: entityId,
      campaign_id: campaignId,
    });
  }, [entityId, campaignId]);

  const downloadParams = useMemo(() => {
    return omitByNil({
      [entityId ? "campaign_target_campaign_id" : "campaign_id"]: campaignId,
      entity_id: entityId,
    });
  }, [campaignId, entityId]);

  const handleAddOrRemoveCampaignContact = useCallback(
    async (contactId, isCampaignTargetContact, invalidateData) => {
      if (isCampaignTargetContact) {
        await CampaignService.removeContact(
          defaultQueryParams.campaign_id,
          defaultQueryParams.entity_id,
          contactId
        );
      } else {
        await CampaignService.addContact(
          defaultQueryParams.campaign_id,
          defaultQueryParams.entity_id,
          contactId
        );
      }
      setIncludedInCampaign(prev => ({
        ...prev,
        [contactId]: !isCampaignTargetContact,
      }));

      await invalidateData();
    },
    [defaultQueryParams]
  );

  const { filters, columns, queryKey, invalidateData } =
    useCampaignContactTable({
      entityId,
      campaignId,
      includedInCampaign,
      tableName,
      activeSession,
      handleAddOrRemoveCampaignContact,
    });

  const fetchCampaignContacts = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);

        let response;
        if (defaultQueryParams.entity_id) {
          response = await CampaignTargetService.getContacts(
            defaultQueryParams.campaign_id,
            defaultQueryParams.entity_id,
            nextQueryParams
          );
        } else {
          response = await ContactService.getContacts({
            ...nextQueryParams,
            ...defaultQueryParams,
          });
        }
        if (response?.data) {
          const includedInCampaign = response.data.reduce((obj, contact) => {
            const isIncluded = isCampaignTargetContact(contact, campaignId);
            return { ...obj, [contact.id]: isIncluded };
          }, {});
          setIncludedInCampaign(includedInCampaign);
        }

        return response;
      } catch (error) {
        console.error("Error fetching campaign contacts:", error);
        throw error;
      }
    },
    [columns, defaultQueryParams, campaignId]
  );

  const actionButtons = useCallback(
    ({ selectedIds, selectedData, uncheckAll }) => (
      <ContactsActionButtons
        selectedIds={selectedIds}
        selectedData={selectedData}
        uncheckAll={uncheckAll}
        invalidateData={invalidateData}
      />
    ),
    [invalidateData]
  );

  return (
    <BaseTableV3
      columns={columns}
      filters={filters}
      headerIcon={CAMPAIGN_CONTACTS_TABLE_CONSTANTS.headerIcon}
      header={CAMPAIGN_CONTACTS_TABLE_CONSTANTS.header}
      queryKey={queryKey}
      className={CAMPAIGN_CONTACTS_TABLE_CONSTANTS.className}
      exportTableName={CAMPAIGN_CONTACTS_TABLE_CONSTANTS.exportTableName}
      tagsDropdownParams={{ visible: enableTags }}
      tableName={tableName}
      downloadParams={downloadParams}
      enableSearch
      enableSettings
      createButton={
        <NewContactButton entityId={entityId} campaignId={campaignId} />
      }
      fetchData={fetchCampaignContacts}
      actionButtons={actionButtons}
      invalidateData={invalidateData}
    />
  );
};

export default CampaignContactsTableV3;
